import { zodResolver } from "@hookform/resolvers/zod"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { MessageSquarePlus, MessagesSquare, Pencil, Pin, PinOff, Trash2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { type Resolver, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { ChatSession } from "@/api/generated/ChatSession"
import {
  useChatAgents,
  useChatSessions,
  useDeleteChatSession,
  useUpdateChatSession,
} from "@/api/queries/chat"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { TextField } from "@/components/app/fields"
import { FormDialog } from "@/components/app/form"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AgentAvatar } from "@/features/chat/agent-avatar"
import { TypingDots } from "@/features/chat/session-list"
import { forgetSession } from "@/features/chat/stream-store"
import { formatNumber } from "@/lib/format"
import { z } from "@/lib/zod"

interface HistorySearch {
  q?: string
  agent?: number
}

export const Route = createFileRoute("/_app/chat/history")({
  staticData: { titleKey: "nav.items.chatHistory" },
  validateSearch: (s: Record<string, unknown>): HistorySearch => {
    const agent = Number(s.agent)
    return {
      q: typeof s.q === "string" && s.q ? s.q : undefined,
      agent: Number.isFinite(agent) && agent > 0 ? agent : undefined,
    }
  },
  component: HistoryPage,
})

function HistoryPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [q, setQ] = useState(search.q ?? "")
  useEffect(() => {
    const id = setTimeout(() => {
      if ((search.q ?? "") !== q)
        void navigate({ search: (s) => ({ ...s, q: q || undefined }), replace: true })
    }, 300)
    return () => clearTimeout(id)
  }, [q, search.q, navigate])

  const sessions = useChatSessions(search.q ?? "")
  const agents = useChatAgents(true)
  const update = useUpdateChatSession()
  const remove = useDeleteChatSession()
  const confirm = useConfirm()
  const [renaming, setRenaming] = useState<ChatSession | null>(null)

  const rows = useMemo(
    () => (sessions.data ?? []).filter((s) => !search.agent || s.agent_id === search.agent),
    [sessions.data, search.agent],
  )

  const onDelete = (s: ChatSession) =>
    confirm({
      title: t("chat.sessions.deleteTitle"),
      description: t("chat.sessions.deleteDescription", { title: s.title || t("chat.sessions.untitled") }),
      confirmLabel: t("chat.sessions.delete"),
      destructive: true,
      onConfirm: async () => {
        await remove.mutateAsync(s)
        forgetSession(s.id)
      },
    })

  const columns = useMemo(
    () =>
      createColumns<ChatSession>((c) => [
        c.accessor((s) => s.title, {
          id: "title",
          header: t("chat.history.columns.title"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="flex min-w-0 items-center gap-2">
              {row.original.pinned && <Pin className="text-muted-foreground size-3.5 shrink-0 -rotate-45" />}
              <span className="truncate font-medium">
                {row.original.title || t("chat.sessions.untitled")}
              </span>
              {row.original.active_run_uid && <TypingDots label={t("chat.sessions.replying")} />}
            </div>
          ),
        }),
        c.accessor("agent_name", {
          header: t("chat.history.columns.agent"),
          meta: { label: t("chat.history.columns.agent") },
          cell: ({ row }) => (
            <div className="flex min-w-0 items-center gap-2">
              <AgentAvatar icon={row.original.agent_icon} size="sm" />
              <span className="truncate">{row.original.agent_name}</span>
            </div>
          ),
        }),
        c.accessor("turn_count", {
          header: t("chat.history.columns.turns"),
          meta: { label: t("chat.history.columns.turns"), align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatNumber(getValue())}</span>,
        }),
        c.accessor("context_compression_enabled", {
          header: t("chat.history.columns.memory"),
          meta: { label: t("chat.history.columns.memory") },
          enableSorting: false,
          cell: ({ row }) =>
            row.original.rolling_summary ? (
              <StatusBadge tone="accent">{t("chat.history.summarized")}</StatusBadge>
            ) : row.original.context_compression_enabled ? (
              <StatusBadge>{t("chat.history.compressionOn")}</StatusBadge>
            ) : (
              <span className="text-muted-foreground text-xs">{t("chat.history.compressionOff")}</span>
            ),
        }),
        c.accessor("updated_at", {
          header: t("chat.history.columns.updated"),
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
        c.accessor("created_at", {
          header: t("chat.history.columns.created"),
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t],
  )

  const agentItems = [
    { value: "all", label: t("chat.history.allAgents") },
    ...(agents.data ?? []).map((a) => ({ value: String(a.id), label: a.name })),
  ]

  return (
    <div className="h-full overflow-y-auto">
      <PageContainer width="wide">
        <PageHeader
          title={t("chat.history.title")}
          description={t("chat.history.description")}
          icon={MessagesSquare}
          actions={
            <Button nativeButton={false} render={<Link to="/chat" />}>
              <MessageSquarePlus />
              {t("chat.newChat")}
            </Button>
          }
        />
        <DataTable
          label={t("chat.history.title")}
          data={rows}
          columns={columns}
          getRowId={(s) => String(s.id)}
          loading={sessions.isPending}
          error={sessions.error}
          onRetry={() => void sessions.refetch()}
          searchable
          search={q}
          onSearchChange={setQ}
          searchPlaceholder={t("chat.sessions.search")}
          persistKey="chat-history"
          columnToggle
          initialColumnVisibility={{ created_at: false }}
          initialSorting={[{ id: "updated_at", desc: true }]}
          toolbar={
            <Select
              items={agentItems}
              value={search.agent ? String(search.agent) : "all"}
              onValueChange={(v) =>
                void navigate({
                  search: (s) => ({ ...s, agent: v && v !== "all" ? Number(v) : undefined }),
                  replace: true,
                })
              }
            >
              <SelectTrigger size="sm" className="min-w-36" aria-label={t("chat.history.columns.agent")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {agentItems.map((i) => (
                  <SelectItem key={i.value} value={i.value}>
                    {i.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
          onRowClick={(s) => void navigate({ to: "/chat/$sessionId", params: { sessionId: String(s.id) } })}
          rowActions={(s) => (
            <>
              <DropdownMenuItem onClick={() => setRenaming(s)}>
                <Pencil />
                {t("chat.sessions.rename")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => update.mutate({ id: s.id, pinned: !s.pinned })}>
                {s.pinned ? <PinOff /> : <Pin />}
                {s.pinned ? t("chat.sessions.unpin") : t("chat.sessions.pin")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={() => void onDelete(s)}>
                <Trash2 />
                {t("chat.sessions.delete")}
              </DropdownMenuItem>
            </>
          )}
          emptyState={
            <EmptyState
              variant="plain"
              icon={MessagesSquare}
              title={t("chat.history.emptyTitle")}
              description={t("chat.history.emptyDescription")}
              action={
                <Button size="sm" nativeButton={false} render={<Link to="/chat" />}>
                  <MessageSquarePlus />
                  {t("chat.newChat")}
                </Button>
              }
            />
          }
        />
        <RenameDialog session={renaming} onClose={() => setRenaming(null)} />
      </PageContainer>
    </div>
  )
}

const renameSchema = z.object({ title: z.string().trim().min(1).max(255) })
type RenameValues = z.infer<typeof renameSchema>

function RenameDialog({ session, onClose }: { session: ChatSession | null; onClose: () => void }) {
  const { t } = useTranslation()
  const update = useUpdateChatSession()
  const form = useForm<RenameValues>({
    resolver: zodResolver(renameSchema) as Resolver<RenameValues>,
    defaultValues: { title: "" },
  })
  useEffect(() => {
    if (session) form.reset({ title: session.title })
  }, [session, form])
  return (
    <FormDialog
      open={!!session}
      onOpenChange={(o) => !o && onClose()}
      form={form}
      title={t("chat.sessions.rename")}
      requireDirty
      onSubmit={async (v) => {
        if (!session) return
        await update.mutateAsync({ id: session.id, title: v.title })
        onClose()
      }}
    >
      <TextField control={form.control} name="title" label={t("chat.history.columns.title")} autoFocus />
    </FormDialog>
  )
}
